/**
 * IT Ticket Creation Page
 *
 * Two-column ServiceNow-style ticket creation form for IT staff.
 *
 * Form layout adapts based on ticket type:
 * - Incident/Request: User-centric (caller, channel, LOB, program, site)
 * - Problem/Change: IT-centric (no caller required, technical focus)
 *
 * Features:
 * - Auto-generated ticket number displayed in professional banner
 * - User search with autofill of LOB, Program, Site (Incident/Request)
 * - Type-specific field sections integrated into columns
 * - Microsoft Fluent-inspired design
 */

import { useState, useCallback, useId, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, AppSnackbar } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { useTicketRepository } from '../../data/hooks';
import { useCurrentUser } from '../../hooks';
import { UserSearch } from '../../components/UserSearch';
import {
  TicketType,
  Priority,
  IT_ONLY_TICKET_TYPES,
  type IncidentCategory,
  type IncidentSubcategory,
  type RequestCategory,
  type RequestSubcategory,
  type ProblemCategory,
  type ProblemSubcategory,
  type RequesterInfo,
  type Location,
  getCategoriesForTicketType,
  getSubcategoriesForCategory,
  getProgramsForLOB,
  TICKET_CHANNELS,
  ASSIGNMENT_GROUPS,
  CLIENT_LOBS,
  getSitesForLOB,
  URGENCIES,
  APPROVAL_STATUSES,
  CHANGE_TYPES,
  CHANGE_CATEGORIES,
  RISK_LEVELS,
  type TicketChannel,
  type AssignmentGroup,
  type ClientLOB,
  type LOBProgram,
  type LOBSite,
  type Urgency,
  type ApprovalStatus,
  type ChangeType,
  type ChangeCategory,
  type RiskLevel,
} from '../../types/ticket';
import type { Department } from '../../types/knowledgeBase';
import type { CreateTicketDTO } from '../../types/dto';
import {
  type ITTicketFormData,
  type ITTicketFormErrors,
  createDefaultITTicketForm,
  generateShortDescription,
  validateITTicketForm,
  hasFormErrors,
  TICKET_VALIDATION,
  URGENCY_DESCRIPTIONS,
  PROBLEM_VALIDATION,
  CHANGE_VALIDATION,
} from '../../types/ticketForms';
import './ITTicketCreationPage.css';

/** Format date for display */
function formatDateTime(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Format ticket number with segments for readability */
function formatTicketNumber(ticketNumber: string): { prefix: string; id: string } {
  const prefix = ticketNumber.substring(0, 3);
  const id = ticketNumber.substring(3);
  return { prefix, id };
}

/** Ticket type descriptions for the selector */
const TICKET_TYPE_INFO: Record<
  TicketType,
  { description: string; color: string; icon: string }
> = {
  Incident: {
    description: 'Something broken or not working',
    color: 'var(--color-error, #d13438)',
    icon: 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z',
  },
  Request: {
    description: 'Request for something new',
    color: 'var(--color-accent-blue, #0078d4)',
    icon: 'M10 1a9 9 0 100 18 9 9 0 000-18zM6.5 9a1.5 1.5 0 113 0v5a1.5 1.5 0 01-3 0V9zm4.5-3a1 1 0 100 2 1 1 0 000-2z',
  },
  Problem: {
    description: 'Root cause analysis',
    color: 'var(--color-purple, #8764b8)',
    icon: 'M10 3a7 7 0 100 14 7 7 0 000-14zm0 1.5a5.5 5.5 0 110 11 5.5 5.5 0 010-11zm-.75 2.25a.75.75 0 011.5 0v3a.75.75 0 01-1.5 0v-3zm.75 5.25a1 1 0 100 2 1 1 0 000-2z',
  },
  Change: {
    description: 'Planned system modification',
    color: 'var(--color-success, #107c10)',
    icon: 'M5.433 13.917l1.262-3.155A4 4 0 017.58 9.42l6.92-6.918a2.121 2.121 0 013 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 01-.65-.65z',
  },
};

/** Change type descriptions */
const CHANGE_TYPE_DESCRIPTIONS: Record<ChangeType, string> = {
  Standard: 'Pre-approved, low-risk routine changes',
  Normal: 'Requires CAB review and approval',
  Emergency: 'Urgent fix for critical issues',
};

/** Risk level descriptions */
const RISK_LEVEL_DESCRIPTIONS: Record<RiskLevel, string> = {
  Low: 'Minimal impact if failure occurs',
  Medium: 'Some business impact possible',
  High: 'Significant impact, requires careful planning',
  Critical: 'Major business risk, executive approval required',
};

export function ITTicketCreationPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const ticketRepo = useTicketRepository();
  const currentUser = useCurrentUser();
  const baseId = useId();

  // Initialize form with current user as opener
  const [formData, setFormData] = useState<ITTicketFormData>(() =>
    createDefaultITTicketForm(currentUser.id, currentUser.name)
  );
  const [errors, setErrors] = useState<ITTicketFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Determine ticket type characteristics
  const isUserCentricType = formData.ticketType === 'Incident' || formData.ticketType === 'Request';
  const isProblemType = formData.ticketType === 'Problem';
  const isChangeType = formData.ticketType === 'Change';

  // Derived options based on form state
  const categoryOptions = useMemo(() => {
    if (isChangeType) return [];
    return getCategoriesForTicketType(formData.ticketType);
  }, [formData.ticketType, isChangeType]);

  const subcategoryOptions = useMemo(() => {
    if (!formData.category || isChangeType) return [];
    return getSubcategoriesForCategory(
      formData.ticketType,
      formData.category as IncidentCategory | RequestCategory | ProblemCategory
    );
  }, [formData.ticketType, formData.category, isChangeType]);

  const programOptions = useMemo(() => {
    if (!formData.clientLOB) return [];
    return getProgramsForLOB(formData.clientLOB as ClientLOB);
  }, [formData.clientLOB]);

  const siteOptions = useMemo(() => {
    if (!formData.clientLOB) return [];
    return getSitesForLOB(formData.clientLOB as ClientLOB);
  }, [formData.clientLOB]);

  // Auto-update short description when relevant fields change (only for user-centric types)
  useEffect(() => {
    if (!isUserCentricType) return;

    const newShortDesc = generateShortDescription(
      formData.caller,
      formData.clientLOB,
      formData.lobProgram,
      formData.lobSite,
      formData.category,
      formData.subcategory
    );
    if (newShortDesc !== formData.shortDescription) {
      setFormData((prev) => ({ ...prev, shortDescription: newShortDesc }));
    }
  }, [
    isUserCentricType,
    formData.caller,
    formData.clientLOB,
    formData.lobProgram,
    formData.lobSite,
    formData.category,
    formData.subcategory,
    formData.shortDescription,
  ]);

  // Field update helper with error clearing
  const updateField = useCallback(
    <K extends keyof ITTicketFormData>(field: K, value: ITTicketFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field as keyof ITTicketFormErrors]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field as keyof ITTicketFormErrors];
          return next;
        });
      }
    },
    [errors]
  );

  // Handle caller (user) selection with comprehensive autofill
  const handleCallerChange = useCallback(
    (user: RequesterInfo | null) => {
      if (user) {
        setFormData((prev) => ({
          ...prev,
          caller: user,
          clientLOB: user.clientLOB ?? prev.clientLOB,
          lobProgram: user.lobProgram ?? prev.lobProgram,
          lobSite: user.lobSite ?? prev.lobSite,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          caller: null,
        }));
      }
      if (errors.caller) {
        setErrors((prev) => ({ ...prev, caller: undefined }));
      }
    },
    [errors.caller]
  );

  // Handle ticket type change (reset category/subcategory and type-specific fields)
  const handleTicketTypeChange = useCallback((type: TicketType) => {
    setFormData((prev) => ({
      ...prev,
      ticketType: type,
      category: '',
      subcategory: '',
      // Clear short description for IT-centric types
      shortDescription: type === 'Problem' || type === 'Change' ? '' : prev.shortDescription,
      // Reset type-specific fields to defaults based on new type
      ...(type === 'Request' && {
        requestApprovalStatus: 'Not Required' as ApprovalStatus,
        requestNeededByDate: '',
        requestedItemName: '',
      }),
      ...(type === 'Problem' && {
        problemStatement: '',
        rootCause: '',
        workaround: '',
        linkedIncidentIds: [],
      }),
      ...(type === 'Change' && {
        changeType: 'Normal' as ChangeType,
        changeCategory: 'Application' as ChangeCategory,
        riskLevel: 'Low' as RiskLevel,
        changeApprovalStatus: 'Pending' as ApprovalStatus,
        rollbackPlan: '',
        implementationStart: '',
        implementationEnd: '',
      }),
    }));
    // Clear type-specific errors
    setErrors({});
  }, []);

  // Handle category change (reset subcategory)
  const handleCategoryChange = useCallback((category: string) => {
    setFormData((prev) => ({
      ...prev,
      category: category as IncidentCategory | RequestCategory | ProblemCategory | '',
      subcategory: '',
    }));
    setErrors((prev) => ({ ...prev, category: undefined, subcategory: undefined }));
  }, []);

  // Handle LOB change (reset program and site if not valid for new LOB)
  const handleLOBChange = useCallback(
    (lob: string) => {
      const newLOB = lob as ClientLOB | '';
      if (!newLOB) {
        setFormData((prev) => ({
          ...prev,
          clientLOB: '',
          lobProgram: '',
          lobSite: '',
        }));
      } else {
        const availablePrograms = getProgramsForLOB(newLOB as ClientLOB);
        const availableSites = getSitesForLOB(newLOB as ClientLOB);
        const currentProgramValid =
          formData.lobProgram && availablePrograms.includes(formData.lobProgram as LOBProgram);
        const currentSiteValid =
          formData.lobSite && availableSites.includes(formData.lobSite as LOBSite);

        setFormData((prev) => ({
          ...prev,
          clientLOB: newLOB,
          lobProgram: currentProgramValid ? prev.lobProgram : '',
          lobSite: currentSiteValid ? prev.lobSite : '',
        }));
      }
      setErrors((prev) => ({ ...prev, clientLOB: undefined }));
    },
    [formData.lobProgram, formData.lobSite]
  );

  // Get risk level CSS class
  const getRiskLevelClass = (level: RiskLevel): string => {
    switch (level) {
      case 'Low':
        return 'it-ticket-form__select--accent-green';
      case 'Medium':
        return 'it-ticket-form__select--accent-yellow';
      case 'High':
        return 'it-ticket-form__select--accent-orange';
      case 'Critical':
        return 'it-ticket-form__select--accent-red';
      default:
        return '';
    }
  };

  // Form validation
  const validateForm = useCallback((): boolean => {
    const validationErrors = validateITTicketForm(formData);
    setErrors(validationErrors);
    return !hasFormErrors(validationErrors);
  }, [formData]);

  // Form submission
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        setSnackbar({
          open: true,
          message: 'Please fix the errors in the form',
          severity: 'error',
        });
        return;
      }

      // Caller validation only for user-centric types
      if (isUserCentricType && !formData.caller) {
        setErrors((prev) => ({ ...prev, caller: 'Please select a user' }));
        return;
      }

      setIsSubmitting(true);

      try {
        // Build base DTO fields
        // Use || instead of ?? to handle empty strings for location
        const resolvedLocation = (formData.caller?.location || formData.lobSite || 'US - Dearborn') as Location;

        const baseDTO = {
          ticketNumber: formData.ticketNumber,
          shortDescription: formData.shortDescription || `${formData.ticketType} - ${formData.description.substring(0, 50)}`,
          priority: formData.priority,
          location: resolvedLocation,
          requesterId: formData.caller?.id ?? currentUser.id,
          requesterName: formData.caller?.name ?? currentUser.name,
          requesterEmail: formData.caller?.email ?? '',
          requesterDepartment: (formData.caller?.department ?? 'IT') as Department,
          createdBy: currentUser.id,
          createdByName: currentUser.name,
          notes: formData.description.trim() || undefined,
        };

        // Build type-specific DTO
        let createDTO: CreateTicketDTO;

        switch (formData.ticketType) {
          case 'Incident':
            createDTO = {
              ...baseDTO,
              ticketType: 'Incident' as const,
              category: formData.category as IncidentCategory,
              subcategory: formData.subcategory as IncidentSubcategory,
            };
            break;

          case 'Request':
            createDTO = {
              ...baseDTO,
              ticketType: 'Request' as const,
              category: formData.category as RequestCategory,
              subcategory: formData.subcategory as RequestSubcategory,
              approvalStatus: formData.requestApprovalStatus,
              neededByDate: formData.requestNeededByDate
                ? new Date(formData.requestNeededByDate)
                : undefined,
              requestedItemName: formData.requestedItemName || undefined,
            };
            break;

          case 'Problem':
            createDTO = {
              ...baseDTO,
              ticketType: 'Problem' as const,
              category: (formData.category || 'Other') as ProblemCategory,
              subcategory: (formData.subcategory || 'Other') as ProblemSubcategory,
              problemStatement: formData.problemStatement,
              rootCause: formData.rootCause || undefined,
              workaround: formData.workaround || undefined,
              linkedIncidentIds: formData.linkedIncidentIds,
            };
            break;

          case 'Change':
            createDTO = {
              ...baseDTO,
              ticketType: 'Change' as const,
              changeCategory: formData.changeCategory,
              changeType: formData.changeType,
              riskLevel: formData.riskLevel,
              approvalStatus: formData.changeApprovalStatus,
              rollbackPlan: formData.rollbackPlan,
              implementationStart: formData.implementationStart
                ? new Date(formData.implementationStart)
                : undefined,
              implementationEnd: formData.implementationEnd
                ? new Date(formData.implementationEnd)
                : undefined,
            };
            break;
        }

        const ticket = await ticketRepo.create(createDTO);

        setSnackbar({
          open: true,
          message: `Ticket ${ticket.ticketNumber} created successfully`,
          severity: 'success',
        });

        setTimeout(() => navigate('/ticketing/all-tickets'), 1200);
      } catch (error) {
        console.error('Failed to create ticket:', error);
        setSnackbar({
          open: true,
          message: 'Failed to create ticket. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, currentUser, ticketRepo, navigate, validateForm, isUserCentricType]
  );

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const ticketNumberParts = formatTicketNumber(formData.ticketNumber);

  // Get minimum date (today) for date pickers
  const today = new Date().toISOString().split('T')[0];

  return (
    <Box className="it-ticket-form" data-theme={theme}>
      <Box className="it-ticket-form__container">
        {/* Header */}
        <Box className="it-ticket-form__header">
          <Box className="it-ticket-form__header-content">
            <Typography variant="h5" component="h1" className="it-ticket-form__title">
              Create Ticket
            </Typography>
            <Typography variant="body2" className="it-ticket-form__subtitle">
              Open a new IT support ticket
            </Typography>
          </Box>
        </Box>

        {/* Professional Ticket Number Banner */}
        <Box className="it-ticket-form__ticket-banner">
          <Box className="it-ticket-form__ticket-badge">
            <svg
              className="it-ticket-form__ticket-icon"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.5 5.833v2.084a2.5 2.5 0 010 4.166v2.084a1.667 1.667 0 01-1.667 1.666H4.167A1.667 1.667 0 012.5 14.167v-2.084a2.5 2.5 0 010-4.166V5.833A1.667 1.667 0 014.167 4.167h11.666A1.667 1.667 0 0117.5 5.833z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.333 4.167v11.666"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="2 2"
              />
            </svg>
            <span className="it-ticket-form__ticket-label">New Ticket</span>
          </Box>
          <Box className="it-ticket-form__ticket-id">
            <span className="it-ticket-form__ticket-prefix">{ticketNumberParts.prefix}</span>
            <span className="it-ticket-form__ticket-number">{ticketNumberParts.id}</span>
          </Box>
          <Box className="it-ticket-form__ticket-meta">
            <span className="it-ticket-form__ticket-status">Draft</span>
          </Box>
        </Box>

        {/* Form */}
        <form className="it-ticket-form__form" onSubmit={handleSubmit} noValidate>
          {/* Ticket Type Selector */}
          <Box className="it-ticket-form__type-section">
            <h3 className="it-ticket-form__section-title">Ticket Type</h3>
            <div className="it-ticket-form__type-grid">
              {(Object.keys(TicketType) as Array<keyof typeof TicketType>).map((key) => {
                const type = TicketType[key];
                const info = TICKET_TYPE_INFO[type];
                const isSelected = formData.ticketType === type;
                const isITOnly = IT_ONLY_TICKET_TYPES.includes(type);

                return (
                  <button
                    key={type}
                    type="button"
                    className={`it-ticket-form__type-card ${isSelected ? 'it-ticket-form__type-card--selected' : ''}`}
                    style={{ '--type-color': info.color } as React.CSSProperties}
                    onClick={() => handleTicketTypeChange(type)}
                    disabled={isSubmitting}
                    aria-pressed={isSelected}
                  >
                    <div className="it-ticket-form__type-icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path d={info.icon} />
                      </svg>
                    </div>
                    <div className="it-ticket-form__type-content">
                      <span className="it-ticket-form__type-name">
                        {type}
                        {isITOnly && <span className="it-ticket-form__type-badge">IT Only</span>}
                      </span>
                      <span className="it-ticket-form__type-description">{info.description}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </Box>

          <Box className="it-ticket-form__columns">
            {/* ============================================
                LEFT COLUMN
                ============================================ */}
            <Box className="it-ticket-form__column">
              {/* === USER-CENTRIC: Caller Information (Incident/Request) === */}
              {isUserCentricType && (
                <Box className="it-ticket-form__section">
                  <h3 className="it-ticket-form__section-title">Caller Information</h3>

                  {/* User Search */}
                  <Box className="it-ticket-form__field">
                    <UserSearch
                      value={formData.caller}
                      onChange={handleCallerChange}
                      label="User"
                      placeholder="Search by name or email..."
                      error={errors.caller}
                      disabled={isSubmitting}
                      required
                    />
                  </Box>

                  {/* Channel */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-channel`} className="it-ticket-form__label">
                      Channel <span className="it-ticket-form__required">*</span>
                    </label>
                    <select
                      id={`${baseId}-channel`}
                      className={`it-ticket-form__select ${errors.channel ? 'it-ticket-form__select--error' : ''}`}
                      value={formData.channel}
                      onChange={(e) => updateField('channel', e.target.value as TicketChannel | '')}
                      disabled={isSubmitting}
                    >
                      <option value="">Select channel</option>
                      {TICKET_CHANNELS.map((channel) => (
                        <option key={channel} value={channel}>
                          {channel}
                        </option>
                      ))}
                    </select>
                    {errors.channel && <span className="it-ticket-form__error">{errors.channel}</span>}
                  </Box>

                  {/* Client LOB */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-lob`} className="it-ticket-form__label">
                      Client LOB <span className="it-ticket-form__required">*</span>
                    </label>
                    <select
                      id={`${baseId}-lob`}
                      className={`it-ticket-form__select ${errors.clientLOB ? 'it-ticket-form__select--error' : ''}`}
                      value={formData.clientLOB}
                      onChange={(e) => handleLOBChange(e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="">Select client</option>
                      {CLIENT_LOBS.map((lob) => (
                        <option key={lob} value={lob}>
                          {lob}
                        </option>
                      ))}
                    </select>
                    {errors.clientLOB && <span className="it-ticket-form__error">{errors.clientLOB}</span>}
                  </Box>

                  {/* LOB Program */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-program`} className="it-ticket-form__label">
                      Program <span className="it-ticket-form__required">*</span>
                    </label>
                    <select
                      id={`${baseId}-program`}
                      className={`it-ticket-form__select ${errors.lobProgram ? 'it-ticket-form__select--error' : ''}`}
                      value={formData.lobProgram}
                      onChange={(e) => updateField('lobProgram', e.target.value as LOBProgram | '')}
                      disabled={isSubmitting || !formData.clientLOB}
                    >
                      <option value="">Select program</option>
                      {programOptions.map((program) => (
                        <option key={program} value={program}>
                          {program}
                        </option>
                      ))}
                    </select>
                    {errors.lobProgram ? (
                      <span className="it-ticket-form__error">{errors.lobProgram}</span>
                    ) : !formData.clientLOB ? (
                      <span className="it-ticket-form__hint">Select client first</span>
                    ) : null}
                  </Box>

                  {/* LOB Site */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-site`} className="it-ticket-form__label">
                      Site <span className="it-ticket-form__required">*</span>
                    </label>
                    <select
                      id={`${baseId}-site`}
                      className={`it-ticket-form__select ${errors.lobSite ? 'it-ticket-form__select--error' : ''}`}
                      value={formData.lobSite}
                      onChange={(e) => updateField('lobSite', e.target.value as LOBSite | '')}
                      disabled={isSubmitting || !formData.clientLOB}
                    >
                      <option value="">Select site</option>
                      {siteOptions.map((site) => (
                        <option key={site} value={site}>
                          {site}
                        </option>
                      ))}
                    </select>
                    {errors.lobSite ? (
                      <span className="it-ticket-form__error">{errors.lobSite}</span>
                    ) : !formData.clientLOB ? (
                      <span className="it-ticket-form__hint">Select client first</span>
                    ) : null}
                  </Box>
                </Box>
              )}

              {/* === PROBLEM TYPE: Problem Analysis Section === */}
              {isProblemType && (
                <Box className="it-ticket-form__section it-ticket-form__section--problem">
                  <h3 className="it-ticket-form__section-title">
                    Problem Analysis
                    <span className="it-ticket-form__badge it-ticket-form__badge--purple">IT Only</span>
                  </h3>

                  {/* Problem Statement */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-problem-statement`} className="it-ticket-form__label">
                      Problem Statement <span className="it-ticket-form__required">*</span>
                    </label>
                    <textarea
                      id={`${baseId}-problem-statement`}
                      className={`it-ticket-form__textarea ${errors.problemStatement ? 'it-ticket-form__textarea--error' : ''}`}
                      value={formData.problemStatement}
                      onChange={(e) => updateField('problemStatement', e.target.value)}
                      placeholder="Describe the problem in detail. What is happening? When did it start? How many users/systems are affected?"
                      disabled={isSubmitting}
                      maxLength={PROBLEM_VALIDATION.MAX_STATEMENT_LENGTH}
                      rows={4}
                    />
                    <Box className="it-ticket-form__field-footer">
                      {errors.problemStatement ? (
                        <span className="it-ticket-form__error">{errors.problemStatement}</span>
                      ) : (
                        <span
                          className={`it-ticket-form__hint ${
                            formData.problemStatement.length >= PROBLEM_VALIDATION.MIN_STATEMENT_LENGTH
                              ? 'it-ticket-form__hint--valid'
                              : ''
                          }`}
                        >
                          {formData.problemStatement.length >= PROBLEM_VALIDATION.MIN_STATEMENT_LENGTH
                            ? 'Statement meets minimum length'
                            : `Minimum ${PROBLEM_VALIDATION.MIN_STATEMENT_LENGTH} characters (${formData.problemStatement.length}/${PROBLEM_VALIDATION.MIN_STATEMENT_LENGTH})`}
                        </span>
                      )}
                      <span className="it-ticket-form__char-count">
                        {formData.problemStatement.length}/{PROBLEM_VALIDATION.MAX_STATEMENT_LENGTH}
                      </span>
                    </Box>
                  </Box>

                  {/* Root Cause */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-root-cause`} className="it-ticket-form__label">
                      Root Cause
                      <span className="it-ticket-form__hint-inline">(Can be updated later)</span>
                    </label>
                    <textarea
                      id={`${baseId}-root-cause`}
                      className="it-ticket-form__textarea"
                      value={formData.rootCause}
                      onChange={(e) => updateField('rootCause', e.target.value)}
                      placeholder="What is the underlying cause? Leave blank if still investigating."
                      disabled={isSubmitting}
                      maxLength={PROBLEM_VALIDATION.MAX_ROOT_CAUSE_LENGTH}
                      rows={3}
                    />
                  </Box>

                  {/* Workaround */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-workaround`} className="it-ticket-form__label">
                      Workaround
                      <span className="it-ticket-form__hint-inline">(Temporary solution)</span>
                    </label>
                    <textarea
                      id={`${baseId}-workaround`}
                      className="it-ticket-form__textarea"
                      value={formData.workaround}
                      onChange={(e) => updateField('workaround', e.target.value)}
                      placeholder="Describe any temporary workarounds users can use."
                      disabled={isSubmitting}
                      maxLength={PROBLEM_VALIDATION.MAX_WORKAROUND_LENGTH}
                      rows={2}
                    />
                  </Box>
                </Box>
              )}

              {/* === CHANGE TYPE: Change Classification Section === */}
              {isChangeType && (
                <Box className="it-ticket-form__section it-ticket-form__section--change">
                  <h3 className="it-ticket-form__section-title">
                    Change Classification
                    <span className="it-ticket-form__badge it-ticket-form__badge--green">IT Only</span>
                  </h3>

                  {/* Change Type */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-change-type`} className="it-ticket-form__label">
                      Change Type <span className="it-ticket-form__required">*</span>
                    </label>
                    <select
                      id={`${baseId}-change-type`}
                      className="it-ticket-form__select"
                      value={formData.changeType}
                      onChange={(e) => updateField('changeType', e.target.value as ChangeType)}
                      disabled={isSubmitting}
                    >
                      {CHANGE_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    <span className="it-ticket-form__hint">{CHANGE_TYPE_DESCRIPTIONS[formData.changeType]}</span>
                  </Box>

                  {/* Change Category */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-change-category`} className="it-ticket-form__label">
                      Change Category <span className="it-ticket-form__required">*</span>
                    </label>
                    <select
                      id={`${baseId}-change-category`}
                      className="it-ticket-form__select"
                      value={formData.changeCategory}
                      onChange={(e) => updateField('changeCategory', e.target.value as ChangeCategory)}
                      disabled={isSubmitting}
                    >
                      {CHANGE_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </Box>

                  {/* Risk Level */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-risk-level`} className="it-ticket-form__label">
                      Risk Level <span className="it-ticket-form__required">*</span>
                    </label>
                    <select
                      id={`${baseId}-risk-level`}
                      className={`it-ticket-form__select ${getRiskLevelClass(formData.riskLevel)}`}
                      value={formData.riskLevel}
                      onChange={(e) => updateField('riskLevel', e.target.value as RiskLevel)}
                      disabled={isSubmitting}
                    >
                      {RISK_LEVELS.map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </select>
                    <span className="it-ticket-form__hint">{RISK_LEVEL_DESCRIPTIONS[formData.riskLevel]}</span>
                  </Box>

                  {/* Approval Status */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-change-approval`} className="it-ticket-form__label">
                      Approval Status <span className="it-ticket-form__required">*</span>
                    </label>
                    <select
                      id={`${baseId}-change-approval`}
                      className="it-ticket-form__select"
                      value={formData.changeApprovalStatus}
                      onChange={(e) => updateField('changeApprovalStatus', e.target.value as ApprovalStatus)}
                      disabled={isSubmitting}
                    >
                      {APPROVAL_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </Box>

                  {/* Implementation Window */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-impl-start`} className="it-ticket-form__label">
                      Implementation Start
                      <span className="it-ticket-form__hint-inline">(Optional)</span>
                    </label>
                    <input
                      id={`${baseId}-impl-start`}
                      type="datetime-local"
                      className="it-ticket-form__input"
                      value={formData.implementationStart}
                      onChange={(e) => updateField('implementationStart', e.target.value)}
                      min={today}
                      disabled={isSubmitting}
                    />
                  </Box>

                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-impl-end`} className="it-ticket-form__label">
                      Implementation End
                      <span className="it-ticket-form__hint-inline">(Optional)</span>
                    </label>
                    <input
                      id={`${baseId}-impl-end`}
                      type="datetime-local"
                      className={`it-ticket-form__input ${errors.implementationEnd ? 'it-ticket-form__input--error' : ''}`}
                      value={formData.implementationEnd}
                      onChange={(e) => updateField('implementationEnd', e.target.value)}
                      min={formData.implementationStart || today}
                      disabled={isSubmitting}
                    />
                    {errors.implementationEnd && (
                      <span className="it-ticket-form__error">{errors.implementationEnd}</span>
                    )}
                  </Box>
                </Box>
              )}

              {/* === CLASSIFICATION SECTION (Incident/Request only) === */}
              {isUserCentricType && (
                <Box className="it-ticket-form__section">
                  <h3 className="it-ticket-form__section-title">Classification</h3>

                  {/* Category */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-category`} className="it-ticket-form__label">
                      Category <span className="it-ticket-form__required">*</span>
                    </label>
                    <select
                      id={`${baseId}-category`}
                      className={`it-ticket-form__select ${errors.category ? 'it-ticket-form__select--error' : ''}`}
                      value={formData.category}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="">Select category</option>
                      {categoryOptions.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                    {errors.category && <span className="it-ticket-form__error">{errors.category}</span>}
                  </Box>

                  {/* Subcategory */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-subcategory`} className="it-ticket-form__label">
                      Subcategory <span className="it-ticket-form__required">*</span>
                    </label>
                    <select
                      id={`${baseId}-subcategory`}
                      className={`it-ticket-form__select ${errors.subcategory ? 'it-ticket-form__select--error' : ''}`}
                      value={formData.subcategory}
                      onChange={(e) =>
                        updateField(
                          'subcategory',
                          e.target.value as IncidentSubcategory | RequestSubcategory | ProblemSubcategory | ''
                        )
                      }
                      disabled={isSubmitting || !formData.category}
                    >
                      <option value="">Select subcategory</option>
                      {subcategoryOptions.map((sub) => (
                        <option key={sub} value={sub}>
                          {sub}
                        </option>
                      ))}
                    </select>
                    {errors.subcategory ? (
                      <span className="it-ticket-form__error">{errors.subcategory}</span>
                    ) : !formData.category ? (
                      <span className="it-ticket-form__hint">Select category first</span>
                    ) : null}
                  </Box>

                  {/* Urgency */}
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-urgency`} className="it-ticket-form__label">
                      Urgency <span className="it-ticket-form__required">*</span>
                    </label>
                    <select
                      id={`${baseId}-urgency`}
                      className="it-ticket-form__select"
                      value={formData.urgency}
                      onChange={(e) => updateField('urgency', e.target.value as Urgency)}
                      disabled={isSubmitting}
                    >
                      {URGENCIES.map((urgency) => (
                        <option key={urgency} value={urgency}>
                          {urgency}
                        </option>
                      ))}
                    </select>
                    <span className="it-ticket-form__hint">{URGENCY_DESCRIPTIONS[formData.urgency]}</span>
                  </Box>

                  {/* Request-Specific: Approval Status */}
                  {formData.ticketType === 'Request' && (
                    <>
                      <Box className="it-ticket-form__field">
                        <label htmlFor={`${baseId}-request-approval`} className="it-ticket-form__label">
                          Approval Status
                        </label>
                        <select
                          id={`${baseId}-request-approval`}
                          className="it-ticket-form__select"
                          value={formData.requestApprovalStatus}
                          onChange={(e) => updateField('requestApprovalStatus', e.target.value as ApprovalStatus)}
                          disabled={isSubmitting}
                        >
                          {APPROVAL_STATUSES.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </Box>

                      <Box className="it-ticket-form__field">
                        <label htmlFor={`${baseId}-needed-by`} className="it-ticket-form__label">
                          Needed By
                          <span className="it-ticket-form__hint-inline">(Optional)</span>
                        </label>
                        <input
                          id={`${baseId}-needed-by`}
                          type="date"
                          className={`it-ticket-form__input ${errors.requestNeededByDate ? 'it-ticket-form__input--error' : ''}`}
                          value={formData.requestNeededByDate}
                          onChange={(e) => updateField('requestNeededByDate', e.target.value)}
                          min={today}
                          disabled={isSubmitting}
                        />
                        {errors.requestNeededByDate && (
                          <span className="it-ticket-form__error">{errors.requestNeededByDate}</span>
                        )}
                      </Box>

                      <Box className="it-ticket-form__field">
                        <label htmlFor={`${baseId}-requested-item`} className="it-ticket-form__label">
                          Requested Item
                          <span className="it-ticket-form__hint-inline">(Optional)</span>
                        </label>
                        <input
                          id={`${baseId}-requested-item`}
                          type="text"
                          className="it-ticket-form__input"
                          value={formData.requestedItemName}
                          onChange={(e) => updateField('requestedItemName', e.target.value)}
                          placeholder="e.g., New laptop, Software license"
                          disabled={isSubmitting}
                          maxLength={200}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>

            {/* ============================================
                RIGHT COLUMN
                ============================================ */}
            <Box className="it-ticket-form__column">
              <Box className="it-ticket-form__section">
                <h3 className="it-ticket-form__section-title">Ticket Details</h3>

                {/* Created (Read-only) */}
                <Box className="it-ticket-form__field">
                  <label className="it-ticket-form__label">Created</label>
                  <div className="it-ticket-form__readonly">{formatDateTime(formData.createdAt)}</div>
                </Box>

                {/* Opened By (Read-only) */}
                <Box className="it-ticket-form__field">
                  <label className="it-ticket-form__label">Opened By</label>
                  <div className="it-ticket-form__readonly">{formData.openedByName}</div>
                </Box>

                {/* Assignment Group */}
                <Box className="it-ticket-form__field">
                  <label htmlFor={`${baseId}-assignment`} className="it-ticket-form__label">
                    Assignment Group <span className="it-ticket-form__required">*</span>
                  </label>
                  <select
                    id={`${baseId}-assignment`}
                    className={`it-ticket-form__select ${errors.assignmentGroup ? 'it-ticket-form__select--error' : ''}`}
                    value={formData.assignmentGroup}
                    onChange={(e) => updateField('assignmentGroup', e.target.value as AssignmentGroup | '')}
                    disabled={isSubmitting}
                  >
                    <option value="">Select assignment group</option>
                    {ASSIGNMENT_GROUPS.map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                  </select>
                  {errors.assignmentGroup && (
                    <span className="it-ticket-form__error">{errors.assignmentGroup}</span>
                  )}
                </Box>

                {/* Site (Optional for Problem/Change) */}
                {(isProblemType || isChangeType) && (
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-site-optional`} className="it-ticket-form__label">
                      Affected Site
                      <span className="it-ticket-form__hint-inline">(Optional)</span>
                    </label>
                    <select
                      id={`${baseId}-site-optional`}
                      className="it-ticket-form__select"
                      value={formData.lobSite}
                      onChange={(e) => updateField('lobSite', e.target.value as LOBSite | '')}
                      disabled={isSubmitting}
                    >
                      <option value="">All sites / Not specific</option>
                      {CLIENT_LOBS.flatMap((lob) => getSitesForLOB(lob)).map((site) => (
                        <option key={site} value={site}>
                          {site}
                        </option>
                      ))}
                    </select>
                  </Box>
                )}

                {/* Category/Subcategory (Optional for Problem) */}
                {isProblemType && (
                  <>
                    <Box className="it-ticket-form__field">
                      <label htmlFor={`${baseId}-problem-category`} className="it-ticket-form__label">
                        Technical Area
                        <span className="it-ticket-form__hint-inline">(Optional)</span>
                      </label>
                      <select
                        id={`${baseId}-problem-category`}
                        className="it-ticket-form__select"
                        value={formData.category}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        disabled={isSubmitting}
                      >
                        <option value="">Select area</option>
                        {categoryOptions.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </Box>

                    {formData.category && (
                      <Box className="it-ticket-form__field">
                        <label htmlFor={`${baseId}-problem-subcategory`} className="it-ticket-form__label">
                          Specific Area
                          <span className="it-ticket-form__hint-inline">(Optional)</span>
                        </label>
                        <select
                          id={`${baseId}-problem-subcategory`}
                          className="it-ticket-form__select"
                          value={formData.subcategory}
                          onChange={(e) =>
                            updateField('subcategory', e.target.value as ProblemSubcategory | '')
                          }
                          disabled={isSubmitting}
                        >
                          <option value="">Select specific area</option>
                          {subcategoryOptions.map((sub) => (
                            <option key={sub} value={sub}>
                              {sub}
                            </option>
                          ))}
                        </select>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              <Box className="it-ticket-form__section">
                <h3 className="it-ticket-form__section-title">Description</h3>

                {/* Short Description (Auto-generated for user-centric, manual for IT-centric) */}
                <Box className="it-ticket-form__field">
                  <label htmlFor={`${baseId}-short-desc`} className="it-ticket-form__label">
                    Short Description
                    {isUserCentricType && <span className="it-ticket-form__badge">Auto-generated</span>}
                  </label>
                  <input
                    id={`${baseId}-short-desc`}
                    type="text"
                    className={`it-ticket-form__input ${isUserCentricType ? 'it-ticket-form__input--readonly' : ''}`}
                    value={formData.shortDescription}
                    onChange={(e) => !isUserCentricType && updateField('shortDescription', e.target.value)}
                    readOnly={isUserCentricType}
                    placeholder={
                      isUserCentricType
                        ? 'Auto-generated from caller and category'
                        : 'Enter a brief summary of the ticket'
                    }
                    maxLength={200}
                  />
                  {isUserCentricType && (
                    <span className="it-ticket-form__hint">
                      Format: Site | Name | Employee ID | LOB | Program | Category | Subcategory
                    </span>
                  )}
                </Box>

                {/* Description */}
                <Box className="it-ticket-form__field">
                  <label htmlFor={`${baseId}-description`} className="it-ticket-form__label">
                    Description <span className="it-ticket-form__required">*</span>
                  </label>
                  <textarea
                    id={`${baseId}-description`}
                    className={`it-ticket-form__textarea ${errors.description ? 'it-ticket-form__textarea--error' : ''}`}
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder={
                      isProblemType
                        ? 'Additional details about the problem investigation...'
                        : isChangeType
                          ? 'Describe the change being made, its purpose, and expected outcome...'
                          : 'Describe the issue or request in detail...'
                    }
                    disabled={isSubmitting}
                    rows={6}
                    maxLength={TICKET_VALIDATION.DETAIL_DESCRIPTION_MAX_LENGTH}
                  />
                  <Box className="it-ticket-form__field-footer">
                    {errors.description ? (
                      <span className="it-ticket-form__error">{errors.description}</span>
                    ) : (
                      <span className="it-ticket-form__hint" />
                    )}
                    <span className="it-ticket-form__char-count">
                      {formData.description.length}/{TICKET_VALIDATION.DETAIL_DESCRIPTION_MAX_LENGTH}
                    </span>
                  </Box>
                </Box>

                {/* Rollback Plan (Change only) */}
                {isChangeType && (
                  <Box className="it-ticket-form__field">
                    <label htmlFor={`${baseId}-rollback`} className="it-ticket-form__label">
                      Rollback Plan <span className="it-ticket-form__required">*</span>
                    </label>
                    <textarea
                      id={`${baseId}-rollback`}
                      className={`it-ticket-form__textarea ${errors.rollbackPlan ? 'it-ticket-form__textarea--error' : ''}`}
                      value={formData.rollbackPlan}
                      onChange={(e) => updateField('rollbackPlan', e.target.value)}
                      placeholder="Describe the steps to revert this change if it fails or causes issues. Include any scripts, commands, or procedures needed."
                      disabled={isSubmitting}
                      maxLength={CHANGE_VALIDATION.MAX_ROLLBACK_LENGTH}
                      rows={4}
                    />
                    <Box className="it-ticket-form__field-footer">
                      {errors.rollbackPlan ? (
                        <span className="it-ticket-form__error">{errors.rollbackPlan}</span>
                      ) : (
                        <span
                          className={`it-ticket-form__hint ${
                            formData.rollbackPlan.length >= CHANGE_VALIDATION.MIN_ROLLBACK_LENGTH
                              ? 'it-ticket-form__hint--valid'
                              : ''
                          }`}
                        >
                          {formData.rollbackPlan.length >= CHANGE_VALIDATION.MIN_ROLLBACK_LENGTH
                            ? 'Plan meets minimum length'
                            : `Minimum ${CHANGE_VALIDATION.MIN_ROLLBACK_LENGTH} characters (${formData.rollbackPlan.length}/${CHANGE_VALIDATION.MIN_ROLLBACK_LENGTH})`}
                        </span>
                      )}
                      <span className="it-ticket-form__char-count">
                        {formData.rollbackPlan.length}/{CHANGE_VALIDATION.MAX_ROLLBACK_LENGTH}
                      </span>
                    </Box>
                  </Box>
                )}

                {/* Linked Incidents (Problem only) */}
                {isProblemType && (
                  <Box className="it-ticket-form__field">
                    <label className="it-ticket-form__label">
                      Linked Incidents
                      <span className="it-ticket-form__hint-inline">
                        ({formData.linkedIncidentIds.length} linked)
                      </span>
                    </label>
                    <div className="it-ticket-form__linked-incidents">
                      {formData.linkedIncidentIds.length === 0 ? (
                        <p className="it-ticket-form__linked-empty">
                          No incidents linked yet. Link incidents from the ticket details view after creation.
                        </p>
                      ) : (
                        <div className="it-ticket-form__linked-chips">
                          {formData.linkedIncidentIds.map((id) => (
                            <span key={id} className="it-ticket-form__linked-chip">
                              {id}
                              <button
                                type="button"
                                className="it-ticket-form__linked-remove"
                                onClick={() =>
                                  updateField(
                                    'linkedIncidentIds',
                                    formData.linkedIncidentIds.filter((i) => i !== id)
                                  )
                                }
                                aria-label={`Remove ${id}`}
                              >
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M4.28 3.22a.75.75 0 00-1.06 1.06L6.94 8l-3.72 3.72a.75.75 0 101.06 1.06L8 9.06l3.72 3.72a.75.75 0 101.06-1.06L9.06 8l3.72-3.72a.75.75 0 00-1.06-1.06L8 6.94 4.28 3.22z" />
                                </svg>
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Actions */}
          <Box className="it-ticket-form__actions">
            <button type="submit" className="it-ticket-form__btn-primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="it-ticket-form__spinner" aria-hidden="true" />
                  Creating...
                </>
              ) : (
                'Create Ticket'
              )}
            </button>
          </Box>
        </form>
      </Box>

      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
        autoHideDuration={4000}
      />
    </Box>
  );
}
